/**
 * Features — what the application shows, and what it keeps folded away.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * Homestead Hunter grew twenty-odd surfaces before anybody had to navigate it
 * as a whole. Every one of them is real work and none of them is wrong, but
 * shown all at once they read as noise: five tabs, ten stacked panels on a
 * listing, a dashboard rail, a debug drawer. The owner could not find the two
 * screens they actually wanted.
 *
 * So the application now starts CLOSED. Nothing but the hunt and this settings
 * page is on by default; every other screen and every detail panel is a switch
 * the owner throws once, deliberately, having read what it does. The catalogue
 * below is that reading material — each entry carries a plain sentence of what
 * the thing IS and a paragraph of when it earns its place, in both languages,
 * because a toggle whose label is three words is a toggle nobody turns on.
 *
 * ── Where it is kept ─────────────────────────────────────────────────────────
 *
 * `localStorage`, per browser, not the server. These are a view preference, not
 * a permission: the server refuses what the account may not do regardless of
 * what is switched on here, and nothing in this file is load-bearing for
 * access. A rule the UI enforces is not enforced — so a screen hidden here is
 * still a screen the API guards on its own terms.
 *
 * Every accessor is wrapped: a browser set to block site data throws on the
 * `localStorage` accessor itself, and this module is imported at boot.
 */

import { useSyncExternalStore } from 'react'
import type { Locale } from './i18n'

/* ────────────────────────────────────────────────────────────────────────────
 * The catalogue
 * ────────────────────────────────────────────────────────────────────────── */

export type FeatureKey =
  /* Screens that get a place in the navigation. */
  | 'nav.board'
  | 'nav.map'
  | 'nav.intake'
  | 'nav.search'
  | 'nav.compare'
  | 'nav.inspect'
  | 'nav.inbox'
  /* What the feed puts above the listings. */
  | 'feed.dashboard'
  | 'feed.contrast'
  /* What a listing shows once you open it. */
  | 'detail.nextAction'
  | 'detail.score'
  | 'detail.cost'
  | 'detail.risk'
  | 'detail.seller'
  | 'detail.diligence'
  | 'detail.transport'
  | 'detail.facts'
  | 'detail.priceHistory'
  | 'detail.documents'
  /* Tools that are not part of the daily loop. */
  | 'tools.setup'
  | 'tools.admin'
  | 'tools.debug'
  | 'tools.install'

export type FeatureGroup = 'screens' | 'feed' | 'detail' | 'tools'

export interface Feature {
  key: FeatureKey
  group: FeatureGroup
  /** `Icon` glyph name, kept loose so this module does not import the icon set. */
  icon: string
  /** The route this switch reveals, if it reveals one. */
  path?: string
  /** Needed before the switch is worth showing at all. */
  capability?: string
  en: { name: string; what: string; why: string }
  es: { name: string; what: string; why: string }
}

/**
 * Every switch, in the order the settings page draws them.
 *
 * `what` is one sentence and answers "what will appear". `why` is the part
 * that matters: when this is worth having on, said in terms of the actual job
 * — standing in a yard, on the phone to a seller, deciding what to drive to.
 */
export const FEATURES: readonly Feature[] = [
  /* ── Screens ──────────────────────────────────────────────────────────── */
  {
    key: 'nav.board',
    group: 'screens',
    icon: 'chart',
    path: '/board',
    en: {
      name: 'Board',
      what: 'Adds a Board tab: every structure you are chasing as a card in a column for the stage it has reached — new, reviewing, contacting the seller, qualifying, transport quote, inspection, negotiating, finalist.',
      why: 'The feed answers "what is for sale". The board answers "what have I got moving, and what has gone quiet". Once you are working more than about five at a time, this is where you notice the container you called about eleven days ago and never chased. The stage is also what drives the next-action prompt on each listing, so moving a card changes what the app tells you to do next.',
    },
    es: {
      name: 'Tablero',
      what: 'Añade la pestaña Tablero: cada estructura que estás persiguiendo como tarjeta en la columna de su etapa — nuevo, revisando, contactando al dueño, cualificando, cotización de transporte, inspección, negociando, finalista.',
      why: 'El feed contesta "qué hay a la venta". El tablero contesta "qué tengo moviéndose y qué se quedó callado". Cuando ya trabajas más de unos cinco a la vez, aquí es donde ves el contenedor que llamaste hace once días y nunca perseguiste. La etapa también decide el próximo paso que la app te sugiere, así que mover una tarjeta cambia lo que te dice que hagas.',
    },
  },
  {
    key: 'nav.map',
    group: 'screens',
    icon: 'pin',
    path: '/map',
    en: {
      name: 'Map',
      what: 'Adds a Map tab that plots every structure where it is sitting right now — the yard, the lot, the dealer — with the score as the pin colour.',
      why: 'Where a thing is standing today is a cost, not a detail. A container in Guaynabo and an identical one in Vieques are the same container and a very different purchase, because one of them needs a barge. The map is the fastest way to see which listings are near you, which are across the island, and which are already on the island you are building on.',
    },
    es: {
      name: 'Mapa',
      what: 'Añade la pestaña Mapa, que marca cada estructura donde está parada ahora mismo — el patio, el solar, el dealer — con la puntuación como color del marcador.',
      why: 'Dónde está parada hoy es un costo, no un detalle. Un contenedor en Guaynabo y otro igual en Vieques son el mismo contenedor y una compra muy distinta, porque uno necesita barcaza. El mapa es la vía más rápida de ver cuáles están cerca, cuáles están cruzando la isla, y cuáles ya están en la isla donde vas a construir.',
    },
  },
  {
    key: 'nav.intake',
    group: 'screens',
    icon: 'plus',
    path: '/intake',
    en: {
      name: 'Add a listing',
      what: 'Adds the “Add” button and the intake form: paste a link from Facebook Marketplace or Clasificados, share one into the app, or type what is in front of you — price, size, municipality, a photo, the seller’s number.',
      why: 'This is how anything gets into the application at all. Use it the moment you see a SE VENDE sign on a container by the road, or somebody sends you a listing on WhatsApp. It takes under a minute standing at the gate, and it deduplicates against what you already have, so pasting the same Clasificados link twice does not create a second copy — it records that the price moved.',
    },
    es: {
      name: 'Añadir un anuncio',
      what: 'Añade el botón “Añadir” y el formulario de entrada: pega un enlace de Facebook Marketplace o Clasificados, comparte uno a la app, o escribe lo que tienes delante — precio, medidas, municipio, una foto, el teléfono del dueño.',
      why: 'Así es como entra cualquier cosa a la aplicación. Úsalo apenas veas un letrero de SE VENDE en un contenedor en la carretera, o cuando alguien te mande un anuncio por WhatsApp. Toma menos de un minuto parado en el portón, y compara con lo que ya tienes, así que pegar el mismo enlace de Clasificados dos veces no crea una copia — anota que el precio cambió.',
    },
  },
  {
    key: 'nav.search',
    group: 'screens',
    icon: 'search',
    path: '/search',
    en: {
      name: 'Search',
      what: 'Adds the search icon to the header and a search screen over titles, descriptions, municipalities, seller names and your own notes.',
      why: 'Worth having once you are past roughly thirty saved listings and “scroll until I recognise it” stops working. It is also the fastest way back to something you only half remember — search the seller’s first name, or “Fajardo”, or a phrase from the description, rather than a listing title you never actually read.',
    },
    es: {
      name: 'Buscar',
      what: 'Añade el ícono de buscar al encabezado y una pantalla de búsqueda sobre títulos, descripciones, municipios, nombres de dueños y tus propias notas.',
      why: 'Vale la pena cuando ya pasas de unos treinta anuncios guardados y “bajar hasta que lo reconozca” deja de funcionar. También es la vía más rápida de volver a algo que recuerdas a medias — busca el nombre del dueño, o “Fajardo”, o una frase de la descripción, en vez del título que nunca leíste.',
    },
  },
  {
    key: 'nav.compare',
    group: 'screens',
    icon: 'people',
    path: '/compare',
    en: {
      name: 'Compare',
      what: 'Adds a Compare screen that puts two to four structures side by side in one table — asking price, landed cost, score, size, condition, title status and open risks.',
      why: 'Turn this on when you are choosing, not browsing. Two trailers a thousand dollars apart are almost never a thousand dollars apart once you add transport, a crane or roll-off, a new roof and a set of tyres — and compare is the screen that makes the difference impossible to miss. It is the one to have open when you are talking the decision through with somebody else.',
    },
    es: {
      name: 'Comparar',
      what: 'Añade la pantalla Comparar, que pone de dos a cuatro estructuras lado a lado en una tabla — precio pedido, costo puesto, puntuación, medidas, condición, título y riesgos abiertos.',
      why: 'Actívalo cuando ya estés escogiendo, no mirando. Dos trailers que se ven a mil dólares uno del otro casi nunca lo están cuando le sumas transporte, grúa o roll-off, techo nuevo y un juego de gomas — y Comparar es la pantalla que hace imposible pasar eso por alto. Es la que quieres abierta cuando hablas la decisión con alguien más.',
    },
  },
  {
    key: 'nav.inspect',
    group: 'screens',
    icon: 'camera',
    path: '/inspect',
    en: {
      name: 'Inspection mode',
      what: 'Adds a full-screen checklist you run while standing at the unit: photograph, note and mark each item pass, fail or unknown — floor, roof, doors, rust, soft spots, appliances — and it keeps working with no signal.',
      why: 'The point of it is that you cannot go back. A viewing is one drive out to somebody’s yard, and the corner you did not photograph is the one the decision turns on three days later. It holds everything in a queue on the phone and uploads when you have bars again, so a viewing in a dead zone is not lost work. Turn it on the day before you go and see anything.',
    },
    es: {
      name: 'Modo inspección',
      what: 'Añade una lista de cotejo a pantalla completa para usar parado frente a la unidad: fotografía, anota y marca cada punto como bien, mal o no sé — piso, techo, puertas, óxido, blandura, enseres — y sigue funcionando sin señal.',
      why: 'El asunto es que no puedes volver. Una visita es un viaje hasta el patio de alguien, y la esquina que no fotografiaste es de la que va a depender la decisión tres días después. Guarda todo en cola en el teléfono y lo sube cuando vuelvas a tener señal, así que una visita en zona muerta no es trabajo perdido. Actívalo el día antes de ir a ver algo.',
    },
  },
  {
    key: 'nav.inbox',
    group: 'screens',
    icon: 'bell',
    path: '/inbox',
    en: {
      name: 'Alerts',
      what: 'Adds the bell to the header and an alerts list: price drops, listings that went off market, a seller who has gone quiet, a diligence answer still outstanding.',
      why: 'This is the part that works while you are not looking. A price cut on a trailer you scored three weeks ago is the best possible moment to call, and it is also exactly the thing you would otherwise find out about a month late. Everything lands in this list first, so it works with no notification permission and no push at all — turning on phone notifications is a separate, later step.',
    },
    es: {
      name: 'Alertas',
      what: 'Añade la campana al encabezado y una lista de alertas: bajadas de precio, anuncios retirados, un dueño que se quedó callado, una pregunta de diligencia sin contestar.',
      why: 'Esta es la parte que trabaja cuando no estás mirando. Una rebaja en un trailer que puntuaste hace tres semanas es el mejor momento para llamar, y también es justo de lo que te enterarías un mes tarde. Todo cae primero en esta lista, así que funciona sin permiso de notificaciones y sin push — activar las notificaciones del teléfono es un paso aparte y posterior.',
    },
  },

  /* ── The feed ─────────────────────────────────────────────────────────── */
  {
    key: 'feed.dashboard',
    group: 'feed',
    icon: 'chart',
    en: {
      name: 'Dashboard rail',
      what: 'Puts a row of counting tiles above the feed — new this week, waiting on you, viewings booked, offers out, gone quiet — and each tile filters the feed to exactly what it counted.',
      why: 'It turns the feed from a list into a starting point. Instead of scrolling, you tap the tile that says four sellers have had no contact in ten days and the feed is already filtered to those four. Worth having on once you have enough volume that you open the app without knowing what you came for.',
    },
    es: {
      name: 'Barra de indicadores',
      what: 'Pone una fila de fichas con conteos encima del feed — nuevos esta semana, esperando por ti, visitas pautadas, ofertas hechas, callados — y cada ficha filtra el feed a exactamente lo que contó.',
      why: 'Convierte el feed de una lista en un punto de partida. En vez de bajar y bajar, tocas la ficha que dice que cuatro dueños llevan diez días sin contacto y el feed ya está filtrado a esos cuatro. Vale la pena cuando ya tienes suficiente volumen como para abrir la app sin saber a qué viniste.',
    },
  },
  {
    key: 'feed.contrast',
    group: 'feed',
    icon: 'alert',
    en: {
      name: 'Cheapest is not cheapest',
      what: 'Shows a strip above the feed whenever the lowest asking price and the lowest true landed cost are two different listings, with both numbers on both.',
      why: 'This is the argument the whole application exists to make, said once, in the place you would otherwise miss it. A $1,800 fifth wheel with a leaking roof, no title and a 90-mile tow is not cheaper than a $4,100 container already sitting on your island — and if you only ever sort by asking price you never find that out. The strip does not appear when the two are the same listing, because then there is nothing to point at.',
    },
    es: {
      name: 'El más barato no es el más barato',
      what: 'Enseña una franja encima del feed cuando el precio pedido más bajo y el costo puesto más bajo son dos anuncios distintos, con ambos números en los dos.',
      why: 'Este es el argumento por el que existe la aplicación, dicho una vez, donde si no lo pasarías por alto. Un fifth wheel de $1,800 con el techo filtrando, sin título y a 90 millas de arrastre no es más barato que un contenedor de $4,100 que ya está en tu isla — y si solo ordenas por precio pedido nunca te enteras. La franja no aparece si son el mismo anuncio, porque entonces no hay nada que señalar.',
    },
  },

  /* ── The listing ──────────────────────────────────────────────────────── */
  {
    key: 'detail.nextAction',
    group: 'detail',
    icon: 'check',
    en: {
      name: 'Next action',
      what: 'Puts one prompt at the top of a listing saying the single next thing to do on it, and who it is waiting on.',
      why: 'A lead dies from ambiguity more often than from a bad number. This reads the stage, the last time you spoke to the seller and the unanswered diligence items and reduces them to one sentence — "call Ramón, nine days since you last did" — so opening a listing never ends in deciding what to decide.',
    },
    es: {
      name: 'Próximo paso',
      what: 'Pone un aviso arriba del anuncio que dice la única próxima cosa que hacer, y por quién se está esperando.',
      why: 'Un prospecto se muere de ambigüedad más que de un mal número. Lee la etapa, la última vez que hablaste con el dueño y las preguntas de diligencia sin contestar, y las reduce a una oración — "llama a Ramón, hace nueve días del último contacto" — para que abrir un anuncio no termine en decidir qué decidir.',
    },
  },
  {
    key: 'detail.score',
    group: 'detail',
    icon: 'star',
    en: {
      name: 'Score',
      what: 'Shows the 0–100 fit score with every factor that produced it — budget, size against your land, condition, whether it can be moved, title, livability — each openable to see what it contributed and how much it weighed.',
      why: 'The score is shorthand for "does this match what I said I wanted", weighted by the search profile in Setup. It is a sorting device, not a verdict, and it is deliberately shown with its working: a unit capped at 40 by one open risk is a completely different thing from one that scored 40 evenly across the board, and only the breakdown tells you which you are looking at. Unknowns are counted as penalties and say so.',
    },
    es: {
      name: 'Puntuación',
      what: 'Enseña la puntuación de 0 a 100 con cada factor que la produjo — presupuesto, tamaño contra tu terreno, condición, si se puede mover, título, habitabilidad — y cada uno se abre para ver cuánto aportó y cuánto pesó.',
      why: 'La puntuación es la forma corta de "¿esto cuadra con lo que dije que quería?", pesada según el perfil de búsqueda en Configuración. Sirve para ordenar, no como veredicto, y se enseña a propósito con su cálculo: una unidad topada en 40 por un riesgo abierto es cosa muy distinta de una que sacó 40 parejo, y solo el desglose te dice cuál estás mirando. Lo desconocido cuenta como penalidad y lo dice.',
    },
  },
  {
    key: 'detail.cost',
    group: 'detail',
    icon: 'chart',
    en: {
      name: 'Landed cost',
      what: 'Shows what it actually costs to have this thing standing on your land and livable — asking price plus transport, permits and escort for an oversize load, crane or roll-off placement, pad and tie-downs, hookups, and the repairs it obviously needs — as a low-to-high range, never a single number.',
      why: 'Turn this on if you turn on nothing else. Asking price is the least informative number on a used structure: a $2,800 office trailer 90 miles away that needs an oversize permit is not cheaper than a $3,450 container delivered free. Every line says whether it is a real quote or the application’s own conservative assumption, and it stays a range on purpose — a single figure invites you to believe a precision nobody has.',
    },
    es: {
      name: 'Costo puesto',
      what: 'Enseña lo que de verdad cuesta tener esto parado en tu terreno y habitable — precio pedido más transporte, permisos y escolta si es carga ancha, grúa o roll-off para colocarlo, plataforma y amarres, conexiones, y los arreglos que obviamente necesita — como un rango de mínimo a máximo, nunca un solo número.',
      why: 'Si no activas ninguna otra cosa, activa esta. El precio pedido es el número menos informativo de una estructura usada: un office trailer de $2,800 a 90 millas que necesita permiso de carga ancha no es más barato que un contenedor de $3,450 con entrega incluida. Cada línea dice si es una cotización real o un supuesto conservador de la aplicación, y se queda como rango a propósito — un número solo te invita a creerte una precisión que nadie tiene.',
    },
  },
  {
    key: 'detail.risk',
    group: 'detail',
    icon: 'alert',
    en: {
      name: 'Risks',
      what: 'Lists the open risks on this unit — a leaking roof, water damage, no title on a titled vehicle, an unpermitted conversion, an oversize width, a seller who does not answer — each with its severity and where the finding came from.',
      why: 'These are the things that make a purchase worth nothing rather than worth less, so they are kept apart from the score and can cap it. The value is the provenance line under each one: "seller states the roof leaks" is something you act on, and "no title mentioned" is something you ask about before you drive anywhere. Have this on before you make an offer on anything.',
    },
    es: {
      name: 'Riesgos',
      what: 'Lista los riesgos abiertos de esta unidad — techo filtrando, daño de agua, sin título en un vehículo que lo requiere, una conversión sin permisos, un ancho fuera de medida, un dueño que no contesta — cada uno con su gravedad y de dónde salió.',
      why: 'Estas son las cosas que hacen que una compra no valga nada, en vez de valer menos, así que se mantienen aparte de la puntuación y pueden toparla. El valor está en la línea de procedencia debajo de cada una: "el dueño dice que el techo filtra" es algo sobre lo que actúas, y "no menciona título" es algo que preguntas antes de manejar hasta allá. Ten esto activo antes de hacer una oferta.',
    },
  },
  {
    key: 'detail.seller',
    group: 'detail',
    icon: 'person',
    en: {
      name: 'Seller and contact history',
      what: 'Shows who is selling, how to reach them, whether they are a dealer, how reliably they answer, and every call, text and WhatsApp message logged against them, newest first.',
      why: 'These are sold by people, not by dealerships, and the same person often has three containers in the same yard. This keeps the thread in one place so you are not scrolling your own phone to remember what was said, and it shows the gap since the last contact — usually the reason a warm lead went cold. Tap-to-call and tap-to-WhatsApp log themselves.',
    },
    es: {
      name: 'Dueño e historial de contacto',
      what: 'Enseña quién vende, cómo llamarle, si es dealer, qué tan confiable es contestando, y cada llamada, texto y mensaje de WhatsApp registrado, lo más nuevo primero.',
      why: 'Esto lo vende gente, no concesionarios, y la misma persona muchas veces tiene tres contenedores en el mismo patio. Mantiene el hilo en un solo sitio para que no busques en tu propio teléfono qué se habló, y enseña cuánto lleva sin contacto — que suele ser la razón por la que un prospecto tibio se enfrió. Los botones de llamar y de WhatsApp se registran solos.',
    },
  },
  {
    key: 'detail.diligence',
    group: 'detail',
    icon: 'ticket',
    en: {
      name: 'Diligence checklist',
      what: 'Adds the questions that have to be answered before you buy — is the floor dry, do the doors seal, is the title in hand, how old are the tyres, will a truck reach your site — each marked pass, fail or unknown with the answer and who gave it.',
      why: 'Useful from the moment you are serious about one specific unit, and mostly noise before that. The list is ordered so the cheapest deal-killer comes first: you want to find out there is no title before you have paid somebody to haul it, not after. An item still marked unknown is a question for the next phone call.',
    },
    es: {
      name: 'Lista de diligencia',
      what: 'Añade las preguntas que hay que contestar antes de comprar — si el piso está seco, si las puertas sellan, si el título está en mano, qué edad tienen las gomas, si un camión llega a tu sitio — cada una marcada bien, mal o no sé, con la contestación y quién la dio.',
      why: 'Sirve desde que te pones serio con una unidad específica, y antes de eso es ruido. La lista está en orden para que lo más barato que mata el negocio salga primero: quieres enterarte de que no hay título antes de pagarle a alguien por arrastrarlo, no después. Un punto que sigue en "no sé" es una pregunta para la próxima llamada.',
    },
  },
  {
    key: 'detail.transport',
    group: 'detail',
    icon: 'ferry',
    en: {
      name: 'Getting it to your land',
      what: 'Works out what it costs and takes to move this specific unit to your specific site — the haul, the permit and escort if it is oversize, the crane or roll-off to set it down, the road in and the turning space at your gate, and the barge if you or it are on Vieques or Culebra.',
      why: 'This is the cost that surprises people, and for the outer islands it can be a third of the purchase. A 12ft-wide mobile home needs a DTOP permit and an escort; a 13ft-tall tiny house needs a route survey for wires and branches; a container already sitting on Vieques skips a barge booking entirely, which is usually the whole reason to prefer it. It needs your land measurements from Setup to say anything useful.',
    },
    es: {
      name: 'Llevarlo a tu terreno',
      what: 'Calcula qué cuesta y qué toma mover esta unidad específica a tu sitio específico — el arrastre, el permiso y la escolta si es carga ancha, la grúa o roll-off para bajarlo, el camino de entrada y el espacio de giro en tu portón, y la barcaza si tú o la unidad están en Vieques o Culebra.',
      why: 'Este es el costo que sorprende a la gente, y para las islas municipio puede ser un tercio de la compra. Una casa móvil de 12 pies de ancho necesita permiso de DTOP y escolta; una tiny house de 13 pies de alto necesita revisar la ruta por cables y ramas; un contenedor que ya está en Vieques se ahorra la barcaza completa, que suele ser toda la razón para preferirlo. Necesita las medidas de tu terreno de Configuración para decir algo útil.',
    },
  },
  {
    key: 'detail.facts',
    group: 'detail',
    icon: 'info',
    en: {
      name: 'Facts and where they came from',
      what: 'Lists every individual fact held about this unit with its source and confidence — the listing said it, the seller said it on the phone, you saw it yourself, a document proves it — and names the ones nobody has established.',
      why: 'This is the honesty panel. It separates "the listing says 40ft", "the seller told me the floor is dry" and "nobody has looked at the floor" — three statements that look identical everywhere else in the application. The list of unknowns is the more useful half: it is a ready-made list of what to ask on the next call, and it is where an unknown that is quietly costing you score points becomes visible.',
    },
    es: {
      name: 'Datos y de dónde salieron',
      what: 'Lista cada dato que se tiene de esta unidad con su fuente y su nivel de certeza — lo dijo el anuncio, lo dijo el dueño por teléfono, lo viste tú, lo prueba un documento — y nombra los que nadie ha establecido.',
      why: 'Este es el panel de la honestidad. Separa "el anuncio dice 40 pies", "el dueño me dijo que el piso está seco" y "nadie ha mirado el piso" — tres cosas que en el resto de la aplicación se ven igual. La lista de lo desconocido es la mitad más útil: es una lista ya hecha de qué preguntar en la próxima llamada, y es donde se ve un desconocido que te está costando puntos en silencio.',
    },
  },
  {
    key: 'detail.priceHistory',
    group: 'detail',
    icon: 'clock',
    en: {
      name: 'Price history',
      what: 'Charts every asking price this listing has carried, when each change happened, and how many days it sat at each number before it moved.',
      why: 'It is negotiating information and very little else, which is exactly why it is worth having. Something that has come down twice in five weeks and still has not sold has a seller who needs it gone; something listed at the same number since March has a seller who does not, and no offer you make will move them. Read this before you decide what to open at.',
    },
    es: {
      name: 'Historial de precio',
      what: 'Grafica cada precio que ha tenido el anuncio, cuándo cambió cada uno, y cuántos días estuvo en ese número antes de moverse.',
      why: 'Es información para negociar y poca cosa más, que es justo por lo que vale tenerla. Algo que ha bajado dos veces en cinco semanas y todavía no se vende tiene un dueño que necesita salir de eso; algo anunciado al mismo número desde marzo tiene un dueño que no, y ninguna oferta lo va a mover. Léelo antes de decidir con cuánto abres.',
    },
  },
  {
    key: 'detail.documents',
    group: 'detail',
    icon: 'door',
    en: {
      name: 'Documents',
      what: 'Adds a place to attach and read the paperwork for this unit — the title, a bill of sale, the transport permit, an inspection report, photographs of the data plate or VIN.',
      why: 'Worth it from the point you have paper. Keeping the title photo against the listing rather than in a phone gallery and an email thread is what makes it findable when the hauler asks for it at seven in the morning. Everything uploaded is retained under the same policy as the rest of the listing.',
    },
    es: {
      name: 'Documentos',
      what: 'Añade un sitio para adjuntar y leer los papeles de esta unidad — el título, la factura de venta, el permiso de transporte, un informe de inspección, fotos de la placa o el VIN.',
      why: 'Vale la pena desde que tienes papeles. Guardar la foto del título pegada al anuncio y no regada entre la galería del teléfono y un correo es lo que la hace encontrable cuando el transportista la pide a las siete de la mañana. Todo lo que subas se guarda bajo la misma política que el resto del anuncio.',
    },
  },

  /* ── Tools ────────────────────────────────────────────────────────────── */
  {
    key: 'tools.setup',
    group: 'tools',
    icon: 'settings',
    path: '/setup',
    capability: 'settings.write',
    en: {
      name: 'Setup and data quality',
      what: 'Adds the setup screen: your land profile — gate width, road width, turning space, overhead clearance, slope — your budget and what you are looking for, and a running list of every number in the application that is still a built-in assumption nobody has checked.',
      why: 'The application ships with conservative placeholders rather than blanks so it works on day one, and this is the screen that admits which ones they are. Two matter more than the rest: without your land measurements the app cannot tell you a 12ft-wide unit will not fit through your gate, and without a budget the score is close to meaningless because its heaviest factor has nothing to weigh against. Twenty minutes here early is worth more than any other twenty minutes in this app.',
    },
    es: {
      name: 'Configuración y calidad de datos',
      what: 'Añade la pantalla de configuración: el perfil de tu terreno — ancho del portón, ancho del camino, espacio de giro, altura libre, pendiente — tu presupuesto y lo que buscas, y una lista viva de cada número de la aplicación que todavía es un supuesto que nadie ha comprobado.',
      why: 'La aplicación viene con valores provisionales conservadores en vez de espacios en blanco para que funcione desde el primer día, y esta es la pantalla que admite cuáles son. Dos importan más que las demás: sin las medidas de tu terreno la app no te puede decir que una unidad de 12 pies no pasa por tu portón, y sin presupuesto la puntuación casi no significa nada porque su factor más pesado no tiene contra qué pesarse. Veinte minutos aquí temprano valen más que cualquier otro rato en esta app.',
    },
  },
  {
    key: 'tools.admin',
    group: 'tools',
    icon: 'shield',
    path: '/admin',
    capability: 'admin.access',
    en: {
      name: 'Admin console',
      what: 'Adds the administration screen: accounts and roles, the activity log, recorded errors, server feature flags, the configured sources and their health, and broadcast messages.',
      why: 'Only relevant if more than one person uses this, or if you are diagnosing something. The two parts worth knowing about are the activity log, which is how you find out who changed a cost assumption and when, and source health, which tells you whether an intake path has quietly stopped working rather than simply having found nothing.',
    },
    es: {
      name: 'Consola de administración',
      what: 'Añade la pantalla de administración: cuentas y roles, el registro de actividad, errores registrados, banderas del servidor, las fuentes configuradas y su salud, y mensajes de difusión.',
      why: 'Solo importa si más de una persona usa esto, o si estás diagnosticando algo. Las dos partes que vale conocer son el registro de actividad, que es como averiguas quién cambió un supuesto de costo y cuándo, y la salud de las fuentes, que te dice si una vía de entrada dejó de funcionar en silencio en vez de simplemente no haber encontrado nada.',
    },
  },
  {
    key: 'tools.debug',
    group: 'tools',
    icon: 'scan',
    capability: 'errors.read',
    en: {
      name: 'Debug drawer',
      what: 'Adds a small tab in the corner that opens a live log of what the app is doing — every API call, its status, how long it took, and any error thrown.',
      why: 'For one situation: something is wrong on a real phone and there is no way to attach a laptop to it. The log is on the device, so you can reproduce the problem where it actually happens and read what failed instead of describing a symptom. It logs no tokens, no passkey material and no request bodies. Leave it off the rest of the time — it sits over the bottom-right corner of every screen.',
    },
    es: {
      name: 'Cajón de diagnóstico',
      what: 'Añade una pestañita en la esquina que abre un registro en vivo de lo que hace la app — cada llamada a la API, su estado, cuánto tardó, y cualquier error.',
      why: 'Para una sola situación: algo está mal en un teléfono de verdad y no hay forma de conectarle una computadora. El registro está en el equipo, así que puedes reproducir el problema donde de verdad pasa y leer qué falló en vez de describir un síntoma. No registra tokens, ni material de passkey, ni cuerpos de peticiones. Déjalo apagado el resto del tiempo — se queda encima de la esquina inferior derecha de todas las pantallas.',
    },
  },
  {
    key: 'tools.install',
    group: 'tools',
    icon: 'home',
    en: {
      name: 'Home screen prompt',
      what: 'Shows a card at the bottom of the feed offering to install the application to your phone’s home screen.',
      why: 'Installed, it gets its own icon, opens without the browser bar, and keeps working offline for everything already loaded — which matters when you are standing in a yard in the middle of the island with one bar. It is off the moment you dismiss it, because an install prompt that reappears every visit is an advert.',
    },
    es: {
      name: 'Aviso de pantalla de inicio',
      what: 'Enseña una tarjeta al final del feed que ofrece instalar la aplicación en la pantalla de inicio del teléfono.',
      why: 'Instalada, tiene su propio ícono, abre sin la barra del navegador, y sigue funcionando sin conexión para todo lo que ya cargó — que importa cuando estás parado en un patio en medio de la isla con una rayita de señal. Se apaga apenas la descartes, porque un aviso de instalación que vuelve en cada visita es un anuncio.',
    },
  },
]

export const FEATURE_KEYS: readonly FeatureKey[] = FEATURES.map((f) => f.key)

const BY_KEY = new Map<FeatureKey, Feature>(FEATURES.map((f) => [f.key, f]))

export function featureFor(key: FeatureKey): Feature {
  return BY_KEY.get(key) as Feature
}

/** The copy for a feature in the reader's language. */
export function featureCopy(feature: Feature, locale: Locale) {
  return locale === 'es-PR' ? feature.es : feature.en
}

/**
 * Presets, so recovering from "I turned everything off" is one tap.
 *
 * `essentials` is what a first-time owner most likely wants: see the land, see
 * what it truly costs, add a lead, get told when something changes.
 */
export const PRESETS: Record<'essentials' | 'everything' | 'nothing', readonly FeatureKey[]> = {
  essentials: [
    'nav.intake',
    'nav.map',
    'nav.inbox',
    'feed.contrast',
    'detail.nextAction',
    'detail.score',
    'detail.cost',
    'detail.risk',
    'detail.seller',
    'tools.setup',
  ],
  everything: FEATURE_KEYS,
  nothing: [],
}

/* ────────────────────────────────────────────────────────────────────────────
 * The store
 * ────────────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'hh.features'

/**
 * ON is the default for every key.
 *
 * This started the other way round — everything off, on the theory that an
 * application you meet one switch at a time is easier to learn. It was the
 * wrong call and the beta proved it in about a minute: what an owner actually
 * meets is a single navigation item and an empty page, with no way to tell an
 * application that is starting closed from one that was never built. The work
 * is extensive and hiding it does not make it findable.
 *
 * So the switches now subtract rather than add. Everything this application can
 * do is visible on the first run; the settings page is where you turn OFF the
 * parts of it you do not use, and every entry still explains itself so that
 * decision is an informed one.
 */
type State = Partial<Record<FeatureKey, boolean>>

function load(): State {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const out: State = {}
    for (const key of FEATURE_KEYS) {
      const value = (parsed as Record<string, unknown>)[key]
      if (typeof value === 'boolean') out[key] = value
    }
    return out
  } catch {
    // A private window, blocked site data, or a truncated write. Everything off
    // is a working application, so there is nothing to report here.
    return {}
  }
}

let state: State = load()

/*
 * The snapshot is a STRING, for the same reason `route.ts` snapshots an href:
 * `useSyncExternalStore` compares with `Object.is`, and returning a fresh
 * object on every read would re-render every subscriber forever.
 */
let snapshot = JSON.stringify(state)

const listeners = new Set<() => void>()

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function getSnapshot(): string {
  return snapshot
}

function commit(next: State): void {
  state = next
  const encoded = JSON.stringify(next)
  if (encoded === snapshot) return
  snapshot = encoded
  try {
    localStorage.setItem(STORAGE_KEY, encoded)
  } catch {
    /* the preference simply will not persist */
  }
  for (const fn of [...listeners]) fn()
}

/** Is this on? For code that is not a component. Absent means ON. */
export function isOn(key: FeatureKey): boolean {
  return state[key] !== false
}

/**
 * Turning something ON removes its entry rather than writing `true`, so the
 * store only ever holds the exceptions. See `applyPreset` for why that matters.
 */
export function setFeature(key: FeatureKey, on: boolean): void {
  const next = { ...state }
  if (on) delete next[key]
  else next[key] = false
  commit(next)
}

/**
 * Apply a preset by storing only the OFFs.
 *
 * A key that should be ON is DELETED rather than written `true`, because absent
 * already means on. Two things fall out of that and both matter: "everything
 * on" stores `{}`, which is byte-for-byte the state of a browser that has never
 * opened this app — so the test asserting the first-run default is testing the
 * real thing rather than a preset that happens to agree with it. And a switch
 * added in a later release is on for everybody, instead of being born hidden
 * for every existing user because their stored object predates its key.
 */
export function applyPreset(name: keyof typeof PRESETS): void {
  const on = new Set(PRESETS[name])
  const next: State = {}
  for (const key of FEATURE_KEYS) if (!on.has(key)) next[key] = false
  commit(next)
}

/** Subscribes. One switch, and the caller re-renders when it moves. */
export function useFeature(key: FeatureKey): boolean {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return isOn(key)
}

/**
 * Subscribes to all of them, and answers about any. The settings page and the
 * shell both need the whole set, and one subscription is cheaper than twenty.
 */
export function useFeatures(): (key: FeatureKey) => boolean {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return isOn
}

/** How many are on. The settings page says it, so "off" never looks broken. */
export function countOn(): number {
  return FEATURE_KEYS.filter(isOn).length
}
